import React from "react";
import moment from "moment";
import styled from "styled-components";
import { UI_CAPTION_DF, colors2, TooltipV2, TextLoader } from "oolib";
import { ArrowUpRight, ArrowDownRight, Minus, AsteriskSimple, CircleDashed } from "@phosphor-icons/react";
import { SIGNAL_CATEGORY_ACCENT } from "@/components/analysisModules/comps/SignalFilters/signalCategories";
import { useSignalCategories } from "./SignalCategoriesContext";

/**
 * The one-line trend under a cluster card's category bar: is the card's
 * LEADING signal category taking a bigger or smaller share of the card than
 * it did in the previous calendar window? Share, not count — a raw count
 * climbs whenever more data lands, share answers "is this topic turning
 * more negative / needy / whatever leads it".
 *
 * Reads as "↗ Rising vs Jul '26", sitting right after the leading category's
 * legend label so the label and its trend form one statement. The exact
 * percentages ride in a hover tooltip for whoever wants them.
 *
 * Colour follows the category's tone (SignalsWidget CONFIG): a rising share
 * of dissatisfaction is bad (red) and a falling one good (green);
 * satisfaction is the mirror; needs and behaviours carry no good/bad, so
 * they stay grey. Steady and new are grey for every category. As with
 * LensesV2's TrendPill, colour never touches the icon or the wording — the
 * direction shown is always the real one.
 */

// Share moves inside this band count as steady.
const STABLE_BAND_PTS = 5;
// Below this many prior observations a share comparison is noise. The chip
// still renders — as "too little data" — so a missing verdict is never
// mistaken for a missing feature.
const MIN_PRIOR_OBSERVATIONS = 5;

const grainOf = (value = "") => {
  if (/^month_|_month$/.test(value)) return "month";
  if (/^year_|_year$/.test(value)) return "year";
  if (/_week$/.test(value)) return "week";
  return null;
};

// The calendar window immediately before a period option, with the short
// label the trend line names it by. Calendar-aligned rather than the BE's
// equal-length window on purpose: "this month" compares against the WHOLE
// of last month, which is what a reader expects and is sound because we
// compare shares, not counts. Null for options with no grain (all time).
export const previousWindowFor = (period) => {
  const grain = grainOf(period?.value);
  if (!grain || !period?.from) return null;
  // Plain moment() = the app's convention: dates are stored/served as UTC
  // and parsed into the browser's local time on the way in — the same way
  // PeriodTimeline and the sparkline bins read these period edges.
  const to = moment(period.from);
  const from = to.clone().subtract(1, grain);
  const label =
    grain === "month" ? from.format("MMM 'YY") : grain === "year" ? from.format("YYYY") : "prior week";
  return { from: from.toISOString(), to: to.toISOString(), label };
};

const sum = (counts = {}) => Object.values(counts).reduce((a, b) => a + b, 0);
const sharePct = (counts, category) => Math.round(((counts[category] || 0) / sum(counts)) * 100);

const TREND_META = {
  rising: { Icon: ArrowUpRight, text: (label) => `Rising vs ${label}` },
  falling: { Icon: ArrowDownRight, text: (label) => `Falling vs ${label}` },
  steady: { Icon: Minus, text: (label) => `Steady vs ${label}` },
  new: { Icon: AsteriskSimple, text: (label) => `New since ${label}` },
  sparse: { Icon: CircleDashed, text: (label) => `Too little data in ${label}` },
};

// Muted by default; the tone colours are the category accent hues, which
// already mean "dissatisfaction" and "satisfaction" everywhere in this view.
const GREY = colors2.grey50;
const RED = SIGNAL_CATEGORY_ACCENT.userDissatisfaction;
const GREEN = SIGNAL_CATEGORY_ACCENT.userSatisfaction;

const colorFor = (kind, tone) => {
  if (kind !== "rising" && kind !== "falling") return GREY;
  if (tone === "good") return kind === "rising" ? GREEN : RED;
  if (tone === "bad") return kind === "rising" ? RED : GREEN;
  return GREY;
};

const Row = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 3px;
  margin-left: 0.2rem;
  color: ${({ $color }) => $color};
  white-space: nowrap;
`;

// `loading`: the previous window is still being counted (Topics V2 fetches it
// after the cards paint) — a caption-sized text loader holds the chip's place
// so the legend line doesn't jump when the verdict lands.
export const LeadingCategoryTrend = ({ category, signalCategoryCounts, prevSignalCategoryCounts, prevWindow, loading = false }) => {
  const categoryTheme = useSignalCategories();
  if (loading && category && prevWindow && !prevSignalCategoryCounts) {
    return <TextLoader style={{ width: "9rem", height: "1.1rem", display: "inline-block", verticalAlign: "middle" }} />;
  }
  if (!category || !prevWindow || !prevSignalCategoryCounts) return null;
  if (!sum(signalCategoryCounts)) return null;

  const prevTotal = sum(prevSignalCategoryCounts);
  const title = categoryTheme.displayOf(category);

  let kind;
  let detail;
  if (!prevTotal) {
    kind = "new";
    detail = `${title}: no observations in ${prevWindow.label}`;
  } else if (prevTotal < MIN_PRIOR_OBSERVATIONS) {
    kind = "sparse";
    detail = `${title}: ${sharePct(signalCategoryCounts, category)}% of ${sum(signalCategoryCounts)} observations now. Only ${prevTotal} observation${prevTotal === 1 ? "" : "s"} in ${prevWindow.label}, fewer than the ${MIN_PRIOR_OBSERVATIONS} needed to compare.`;
  } else {
    const nowTotal = sum(signalCategoryCounts);
    const nowPct = sharePct(signalCategoryCounts, category);
    const prevPct = sharePct(prevSignalCategoryCounts, category);
    const delta = nowPct - prevPct;
    kind = delta > STABLE_BAND_PTS ? "rising" : delta < -STABLE_BAND_PTS ? "falling" : "steady";
    detail = `${title}: ${nowPct}% of ${nowTotal} observations now, ${prevPct}% of ${prevTotal} in ${prevWindow.label}`;
  }

  const { Icon, text } = TREND_META[kind];
  const color = colorFor(kind, categoryTheme.styleOf(category).tone);
  return (
    <TooltipV2 text={detail} placement="bottom-start" wrapperDisplay="inline-block">
      <Row $color={color}>
        <Icon size={12} weight="bold" color={color} />
        <UI_CAPTION_DF>{text(prevWindow.label)}</UI_CAPTION_DF>
      </Row>
    </TooltipV2>
  );
};
